import React, { useCallback } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { themeOptions } from "styles/theme";
import { format } from "../../../lib/globalize-util";
import { DATATEST } from "constants/integration";
import Select from "components/form/select-field";
import MenuItem from "material-ui/MenuItem";

const { PRINT_TYPE_VIEW, PRINT_TYPE_SELECT, PRINT_TYPE_OPTION } = DATATEST.CUSTOMIZATIONS;

interface IPrintTypeLabelProps {
    name: string;
}

export const PrintTypeLabel: React.FC<IPrintTypeLabelProps> = ({ name }) => {
    const classes = useStyles();

    return <div className={classes.printTypeLabel}>{format(name)}</div>;
};

interface IPrintTypeProps {
    printType?: string;
    readonly?: boolean;
    types: string[];
    onChange?: (value: string) => void;
}

const usePrintType = (printType?: string, types?: string[]) => printType || types?.[0] || "";

export const PrintType: React.FC<IPrintTypeProps> = ({ printType, types, readonly }) => {
    const classes = useStyles();
    const type = usePrintType(printType, types);

    return (
        <React.Fragment>
            <PrintTypeLabel name={"Customization Type:"} />
            <div className={classes.printType} data-test={PRINT_TYPE_VIEW}>
                {type}
            </div>
        </React.Fragment>
    );
};

export const PrintTypes: React.FC<IPrintTypeProps> = ({ printType, types, onChange, readonly }) => {
    const type = usePrintType(printType, types);
    const allowedTypes = readonly ? [type] : types;
    const handleTypeChange = useCallback(
        (event, index, type) => {
            onChange?.(type);
        },
        [onChange],
    );

    return (
        <React.Fragment>
            <PrintTypeLabel name={"Select Customization Type:"} />
            <Select
                disabled={readonly}
                name="types"
                value={type}
                large={false}
                style={selectStyles.printType}
                labelStyle={selectStyles.printTypeLabel}
                onChange={handleTypeChange}
                data-test={PRINT_TYPE_SELECT}
            >
                {allowedTypes.map((type, index) => (
                    <MenuItem key={type} value={type} primaryText={type} data-test={`${PRINT_TYPE_OPTION}_${index}`} />
                ))}
            </Select>
        </React.Fragment>
    );
};

export const PrintedAttributePrintTypes: React.FC<IPrintTypeProps> = (props) => {
    const classes = useStyles();

    const { types, readonly } = props;
    const length = types?.length;
    if (!length || length === 0) {
        return null;
    }

    const render = () => {
        if (length === 1 || readonly) {
            return <PrintType {...props} />;
        }

        return <PrintTypes {...props} />;
    };

    return <div className={classes.typeContainer}>{render()}</div>;
};

const useStyles = makeStyles({
    printTypeLabel: {
        color: themeOptions.palette.textColor,
        fontSize: 12,
        fontWeight: 700,
        marginBlockEnd: 10,
    },
    printType: {
        fontSize: 14,
        "&:first-letter": {
            textTransform: "uppercase",
        },
        textTransform: "lowercase",
        color: themeOptions.palette.darkGrey,
    },
    typeContainer: {
        display: "flex",
        flexDirection: "column",
    },
});

const selectStyles = {
    printType: {
        width: "45%",
    },
    printTypeLabel: {
        color: themeOptions.palette.textColor,
        marginBottom: 8,
    },
};
